import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import { TablePaginatedV8, order2Sorting, TableCellV8, ButtonNewRecord } from 'motion-components';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { CollectionOrderStates } from 'shared-pages/common/constants';
import { isFotokiklosi, isRebattery } from 'shared-pages/common/constants';
import { FormatDate } from 'shared-pages/common/date-helpfunc';
import { CollectionOrderStatusBadge } from 'shared-pages/pages/collection-orders/collection-order-status-badge';
import { Button } from 'react-bootstrap';
import { promiseModalCollectionPointCollections } from './collections/CollectionModal';
import { formatFloat } from 'shared-pages/utils/helpers';
import { promiseModalRoutes } from './routes/RouteModal';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const handleNewCollectionΟrder = useCallback(
    ({ id }: { id: number }) => {
      if (!id) return;
      // console.log('id is', id);
      history.push('/order/-1', { collectionPointId: id });
    },
    [history], // Dependency array ensures `history` reference is stable
  );
  const openCollectionsModal = async (collectionPointId, collectionpoint) => {
    const result = await promiseModalCollectionPointCollections({
      collectionPointId,
      collectionpoint,
    });
    //console.log('Selected Collection:', result);
  };
  const openRouteModal = async (collectionOrderId, collectorId) => {
    const result = await promiseModalRoutes({
      collectionOrderId,
      collectorId,
    });
  };
  const columns = useMemo(
    () => [
      ...(!isRebattery
        ? [
            {
              id: 'collectionpoint.code',
              header: t('Κωδικός'),
              columnName: t('Κωδικός'),
              accessorKey: 'collectionpoint.code',
              orderByField: 'collectionpoint.code',
              cell: TableCellV8,
              size: 10,
            },
          ]
        : []),
      {
        id: 'collectionpoint.name',
        header: t('Επωνυμία'),
        columnName: t('Επωνυμία'),
        accessorKey: 'collectionpoint.name',
        orderByField: 'collectionpoint.name',
        cell: ({ row }) => {
          const id = row.original.collectionpointId;
          const cp = row.original.collectionpoint;

          const path = generatePath(getRouteByName('Collectionpoint'), {
            id: id ?? 0,
          });
          return (
            <>
              <Link to={path}>{cp.name}</Link>
              <div>
                <small>{cp.cpGroup?.name}</small>
              </div>
            </>
          );
        },
        size: 30,
      },
      ...(isFotokiklosi
        ? [
            {
              id: 'collectionpoint.addressDescription',
              header: t('Περιγραφή Διεύθυνσης'),
              columnName: t('Περιγραφή Διεύθυνσης'),
              accessorKey: 'collectionpoint.addressDescription',
              orderByField: 'collectionpoint.addressDescription',
              cell: TableCellV8,
              size: 20,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'collectionpoint.addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'collectionpoint.addressLine1',
        orderByField: 'collectionpoint.addressLine1',
        cell: ({ row }) => {
          const id = row.original.collectionpointId;
          const cp = row.original.collectionpoint;

          const path = generatePath(getRouteByName('Collectionpoint'), {
            id: id ?? 0,
          });
          return (
            <>
              <div>{cp.addressLine1}</div>
              <div>
                {cp.addressArea} {cp.addressCity}
              </div>
              <div>{cp.addressNomos}</div>
            </>
          );
        },
        size: 40,
        meta: { type: 'text' },
      },
      {
        id: 'lastCollectionOrder.code',
        header: t('Τελευταία Αίτηση Συλλογής'),
        columnName: t('Τελευταία Αίτηση Συλλογής'),
        accessorKey: 'lastCollectionOrder.code',
        orderByField: 'lastCollectionOrder.code',
        cell: ({ row }) => {
          // console.log(row.original);
          const lastCollectionOrder = row.original.lastCollectionOrder;
          if (!lastCollectionOrder) return <></>;
          const path = generatePath(getRouteByName('Order'), {
            id: lastCollectionOrder.id ?? 0,
          });
          return (
            <>
              <Link to={path}>{lastCollectionOrder.code}</Link>
              <div className="pt-1 pb-1">
                {CollectionOrderStatusBadge(lastCollectionOrder?.collectionOrderState)}
              </div>
              <div>{FormatDate(lastCollectionOrder.orderedDate)}</div>
              {lastCollectionOrder.routesLines.length > 0 && (
                <div className="pt-1 pb-1">
                  {lastCollectionOrder.routesLines.map((routeLine) => (
                    <div key={routeLine.id}>
                      <Link to={`/route/${routeLine.routeId}`}>
                        Δρομολόγιο: {routeLine.route.code}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        },
        size: 50,
      },
      {
        id: 'lastCollection.code',
        header: t('Τελευταία Συλλογή'),
        columnName: t('Τελευταία Συλλογή'),
        accessorKey: 'lastCollection.code',
        orderByField: 'lastCollection.code',
        cell: ({ row }) => {
          // console.log(row.original);
          const lastCollection = row.original.lastCollection;
          if (!lastCollection) return <></>;
          const path = generatePath(getRouteByName('Collection'), { id: lastCollection.id ?? 0 });
          return (
            <>
              <div>
                <Link to={path}>{lastCollection.code}</Link>
              </div>
              <div>{FormatDate(lastCollection.dateCollected)}</div>
              <div className="mt-1">
                <Button
                  variant="outline-primary"
                  onClick={() => openCollectionsModal(row.original.collectionpointId, row.original)}
                  size="sm"
                >
                  {t('Συλλογές Σημείου')}
                </Button>
              </div>
            </>
          );
        },
        size: 50,
      },
      {
        id: 'daysPassed',
        header: t('Ημέρες από Τελ. Συλλογή (β)'),
        columnName: t('Ημέρες από Τελ. Συλλογή (β)'),
        accessorKey: 'daysPassed',
        orderByField: 'daysPassed',
        cell: ({ row }) => {
          return <div className="text-center">{row.original.daysPassed}</div>;
        },
        size: 10,
      },
      {
        id: 'movingAvgCollectionInterval',
        header: t('Μέσες Ημέρες μεταξυ 5 Τελ. Συλλογών (α)'),
        columnName: t('Μέσες Ημέρες μεταξυ 5 Τελ. Συλλογών (α)'),
        accessorKey: 'movingAvgCollectionInterval',
        orderByField: 'movingAvgCollectionInterval',
        cell: ({ row }) => {
          return <div className="text-center">{row.original.movingAvgCollectionInterval}</div>;
        },
        size: 10,
      },
      {
        id: 'priorityIndex',
        header: t('Δείκτης Προτεραιότητας (β / α)'),
        columnName: t('Δείκτης Προτεραιότητας (β / α)'),
        accessorKey: 'priorityIndex',
        orderByField: 'priorityIndex',
        cell: ({ row }) => {
          return <div className="text-center">{formatFloat(row.original.priorityIndex, 2)}</div>;
        },
        size: 10,
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        orderByField: 'lastUser',
        size: 120,
        cell: ({ row, table }) => {
          const lastCollectionOrder = row.original.lastCollectionOrder;
          const hasLastOrder =
            lastCollectionOrder &&
            row.original.lastCollectionOrder.status !== CollectionOrderStates.Executed;
          // Δρομολόγηση όταν έχει τελευταία αίτηση συλλογής και είναι σε κατάσταση NotRouted
          const enableRouting =
            lastCollectionOrder &&
            lastCollectionOrder?.collectionOrderState?.value === CollectionOrderStates.NotRouted;

          return (
            <div className="d-flex pt-4">
              {/* Νέα Αίτηση Συλλογής */}
              <ButtonNewRecord
                className={`align-self-center me-3 ${!hasLastOrder ? 'btn-info' : 'btn-light'}`}
                label="Αίτηση Συλλογής"
                onClick={() => handleNewCollectionΟrder({ id: row.original.collectionpointId })}
                disabled={hasLastOrder}
              />
              {/* Προσθήκη σε Δρομολόγιο */}
              <ButtonNewRecord
                className={`align-self-center me-3 ${enableRouting ? 'btn-success' : 'btn-light'}`}
                label="Σε Δρομολόγιο"
                onClick={() =>
                  openRouteModal(
                    row.original.lastCollectionOrder?.id,
                    row.original.lastCollectionOrder?.collectorId,
                  )
                }
                disabled={!enableRouting}
              />
            </div>
          );
        },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
